import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { priorityColor } from "@/lib/utils";
import { toast } from "sonner";
import { ShieldCheck, CalendarBlank, FloppyDisk, ArrowFatLinesUp, ArrowsClockwise } from "@phosphor-icons/react";
import { SlaPlansManager } from "@/components/sla/SlaPlansManager";
import { BusinessCalendarsManager } from "@/components/sla/BusinessCalendarsManager";

const DAYS = [["Mon", 0], ["Tue", 1], ["Wed", 2], ["Thu", 3], ["Fri", 4], ["Sat", 5], ["Sun", 6]];

export default function SLAConfig() {
  const [policies, setPolicies] = useState([]);
  const [cal, setCal] = useState(null);
  const [esc, setEsc] = useState(null);
  const [recomputing, setRecomputing] = useState(false);
  const load = () => api.get("/sla-policies").then(r => setPolicies(r.data));
  useEffect(() => {
    load();
    api.get("/settings/business-hours").then(r => setCal(r.data));
    api.get("/settings/escalation").then(r => setEsc({ ...r.data, notifyText: (r.data.notify_emails || []).join(", ") })).catch(() => {});
  }, []);

  const saveEsc = async () => {
    try {
      const payload = {
        enabled: esc.enabled, bump_priority: esc.bump_priority, grace_minutes: esc.grace_minutes,
        pre_breach_minutes: esc.pre_breach_minutes || 0,
        notify_emails: (esc.notifyText || "").split(",").map(s => s.trim()).filter(Boolean),
      };
      const { data } = await api.put("/settings/escalation", payload);
      setEsc({ ...data, notifyText: (data.notify_emails || []).join(", ") });
      toast.success("Auto-escalation settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed (admin only)");
    }
  };

  const update = async (id, patch) => {
    try {
      await api.put(`/sla-policies/${id}`, patch);
      toast.success("SLA updated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed (admin only)");
    }
  };

  const toggleDay = (d) => setCal(c => ({
    ...c, work_days: c.work_days.includes(d) ? c.work_days.filter(x => x !== d) : [...c.work_days, d].sort(),
  }));

  const saveCal = async () => {
    try {
      const { data } = await api.put("/settings/business-hours", cal);
      setCal(data);
      toast.success("Business-hours calendar saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed (admin only)");
    }
  };

  const recomputeSlas = async () => {
    setRecomputing(true);
    try {
      const { data } = await api.post("/sla/recompute");
      toast.success(`SLAs recalculated on ${data.updated} open ticket(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recalculation failed (admin only)");
    } finally { setRecomputing(false); }
  };

  return (
    <div className="p-6" data-testid="sla-view">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-primary mb-1">Service Level Agreements</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black">SLA Policies</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Configure default target response and resolution times per priority, plus reusable <span className="text-primary">SLA Plans</span> and <span className="text-primary">business-hour calendars</span> you can tag to individual clients on the Routing Rules page. Editing a calendar, plan or client mapping recalculates open tickets automatically; use <span className="text-primary">Recalculate SLAs</span> to refresh on demand. Admins only.
          </p>
        </div>
        <Button data-testid="recalculate-slas-btn" onClick={recomputeSlas} disabled={recomputing} variant="outline" className="rounded-sm font-mono text-xs uppercase tracking-widest shrink-0">
          <ArrowsClockwise size={15} className={`mr-2 ${recomputing ? "animate-spin" : ""}`} />{recomputing ? "Recalculating…" : "Recalculate SLAs"}
        </Button>
      </div>

      {cal && (
        <Card className="p-6 border-primary/40 rounded-sm mb-6 bg-primary/5" data-testid="business-hours-card">
          <div className="flex items-center gap-2 mb-4">
            <CalendarBlank size={16} className="text-primary" />
            <span className="text-[11px] font-mono uppercase tracking-widest text-primary">Default Business-Hours Calendar</span>
            <span className="text-[10px] font-mono text-muted-foreground">(fallback for clients with no calendar tagged)</span>
          </div>
          <div className="flex flex-wrap items-end gap-6">
            <div>
              <Label className="text-[10px] font-mono uppercase text-muted-foreground">Work days</Label>
              <div className="flex gap-1.5 mt-1">
                {DAYS.map(([label, d]) => (
                  <button
                    key={d}
                    data-testid={`bh-day-${d}`}
                    onClick={() => toggleDay(d)}
                    className={`h-9 w-11 rounded-sm border text-xs font-mono transition-colors ${cal.work_days.includes(d) ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-muted-foreground hover:border-primary/60'}`}
                  >{label}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase text-muted-foreground">Start hour (IST)</Label>
              <Input type="number" min="0" max="23" data-testid="bh-start" className="mt-1 rounded-sm font-mono w-24" value={cal.start_hour} onChange={(e) => setCal({ ...cal, start_hour: parseInt(e.target.value || 0) })} />
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase text-muted-foreground">End hour (IST)</Label>
              <Input type="number" min="0" max="24" data-testid="bh-end" className="mt-1 rounded-sm font-mono w-24" value={cal.end_hour} onChange={(e) => setCal({ ...cal, end_hour: parseInt(e.target.value || 0) })} />
            </div>
            <Button data-testid="bh-save" onClick={saveCal} className="rounded-sm font-mono uppercase text-xs h-9"><FloppyDisk size={13} className="mr-2" />Save Calendar</Button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-3">Overnight windows allowed — set an end hour earlier than the start (e.g. 20 → 8) for a night shift.</p>
        </Card>
      )}

      {esc && (
        <Card className="p-6 border-border rounded-sm mb-6" data-testid="escalation-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ArrowFatLinesUp size={16} className="text-primary" />
              <span className="text-[11px] font-mono uppercase tracking-widest text-primary">SLA-Breach Auto-Escalation</span>
            </div>
            <Switch data-testid="esc-enabled" checked={esc.enabled} onCheckedChange={(v) => setEsc({ ...esc, enabled: v })} />
          </div>
          <p className="text-xs text-muted-foreground mb-4">When an open ticket is close to (or blows) its resolution SLA, automatically bump its priority, log an internal note, and fire a Teams alert. Runs every 5 minutes. Set a pre-breach warning time to escalate <span className="text-primary">before</span> the deadline.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
            <div>
              <Label className="text-[10px] font-mono uppercase text-muted-foreground">Bump priority</Label>
              <div className="mt-1 flex items-center gap-2 h-10">
                <Switch data-testid="esc-bump" checked={esc.bump_priority} onCheckedChange={(v) => setEsc({ ...esc, bump_priority: v })} />
                <span className="text-xs text-muted-foreground">One level (P3→P2…)</span>
              </div>
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase text-muted-foreground">Before breach (min)</Label>
              <Input type="number" min="0" data-testid="esc-prebreach" className="mt-1 rounded-sm font-mono w-full h-10" value={esc.pre_breach_minutes ?? 0} onChange={(e) => setEsc({ ...esc, pre_breach_minutes: parseInt(e.target.value || 0) })} title="0 = escalate only after breach" />
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase text-muted-foreground">Grace after breach (min)</Label>
              <Input type="number" min="0" data-testid="esc-grace" className="mt-1 rounded-sm font-mono w-full h-10" value={esc.grace_minutes} onChange={(e) => setEsc({ ...esc, grace_minutes: parseInt(e.target.value || 0) })} />
            </div>
            <div className="lg:col-span-2">
              <Label className="text-[10px] font-mono uppercase text-muted-foreground">Notify emails on escalation</Label>
              <Input data-testid="esc-notify" className="mt-1 rounded-sm font-mono text-xs w-full h-10" placeholder="manager@acme.com, itops@acme.com" value={esc.notifyText || ""} onChange={(e) => setEsc({ ...esc, notifyText: e.target.value })} />
            </div>
          </div>
          <div className="flex items-center justify-between mt-3">
            <span className="text-[10px] text-muted-foreground">"Before breach" 0 = escalate only after the SLA is breached. Runs every 5 minutes.</span>
            <Button data-testid="esc-save" onClick={saveEsc} className="rounded-sm font-mono uppercase text-xs h-9"><FloppyDisk size={13} className="mr-2" />Save</Button>
          </div>
        </Card>
      )}

      <div className="flex items-center gap-2 mb-3 mt-8">
        <ShieldCheck size={16} className="text-primary" />
        <span className="text-[11px] font-mono uppercase tracking-widest text-primary">Default SLA Targets</span>
        <span className="text-[10px] font-mono text-muted-foreground">(fallback for clients with no plan tagged)</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {policies.map(p => (
          <Card key={p.id} className="p-6 border-border rounded-sm" data-testid={`sla-card-${p.priority}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <span className={`text-xs font-mono px-2 py-1 border ${priorityColor(p.priority)}`}>{p.priority}</span>
                <div className="font-display text-lg">{p.name}</div>
              </div>
              <ShieldCheck size={18} className="text-primary" />
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-[10px] font-mono uppercase text-muted-foreground">Response target (minutes)</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="number"
                    data-testid={`sla-response-${p.priority}`}
                    className="rounded-sm font-mono"
                    defaultValue={p.response_minutes}
                    onBlur={(e)=>update(p.id, { response_minutes: parseInt(e.target.value) })}
                  />
                </div>
              </div>
              <div>
                <Label className="text-[10px] font-mono uppercase text-muted-foreground">Resolution target (minutes)</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="number"
                    data-testid={`sla-resolution-${p.priority}`}
                    className="rounded-sm font-mono"
                    defaultValue={p.resolution_minutes}
                    onBlur={(e)=>update(p.id, { resolution_minutes: parseInt(e.target.value) })}
                  />
                </div>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-border">
                <Label className="text-xs font-mono uppercase text-muted-foreground">Business hours only</Label>
                <Switch
                  data-testid={`sla-bh-${p.priority}`}
                  checked={p.business_hours_only}
                  onCheckedChange={(v)=>update(p.id, { business_hours_only: v })}
                />
              </div>
              <Button
                variant="outline"
                data-testid={`sla-save-${p.priority}`}
                onClick={()=>toast.info("Changes save automatically on blur.")}
                className="w-full rounded-sm font-mono text-xs uppercase tracking-widest"
              >
                Autosave enabled
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <SlaPlansManager />
      <BusinessCalendarsManager />


    </div>
  );
}
